/** A callback that receives the published data of type `T`. */
type Observer<T> = (data: T) => void;

/**
 * Minimal pub/sub contract. Consumers add/remove themselves; the producer
 * calls `notifyObservers` to push a new value to all current subscribers.
 */
interface Subject<T> {
  addObserver: (observer: Observer<T>) => void;
  removeObserver: (observer: Observer<T>) => void;
  notifyObservers: (data: T) => void;
}

/**
 * Creates a lightweight pub/sub subject backed by a `Set` of observers.
 *
 * Using a `Set` instead of an array means duplicate registrations are silently
 * ignored and removal is O(1) — no index bookkeeping needed.
 *
 * Intended to be used outside of React so state can live in plain modules
 * (e.g. `CartService`) and be consumed via `useSyncExternalStore`.
 *
 * @example
 * const subject = createSubject<number>();
 * const unsub = subject.addObserver((n) => console.log(n));
 * subject.notifyObservers(42); // logs 42
 * unsub();                     // or: subject.removeObserver(fn)
 */
export function createSubject<T>(): Subject<T> {
  const observers = new Set<Observer<T>>();

  /**
   * Registers `observer` and returns an unsubscribe function.
   * Registering the same function reference twice has no effect.
   */
  const addObserver = (observer: Observer<T>) => {
    observers.add(observer);
    return () => {
      observers.delete(observer);
    };
  };

  /** Removes `observer` if it was previously registered; no-op otherwise. */
  const removeObserver = (observer: Observer<T>) => {
    observers.delete(observer);
  };

  /** Synchronously calls every registered observer with `data`. */
  const notifyObservers = (data: T) => {
    observers.forEach((observer) => observer(data));
  };

  return {
    addObserver,
    removeObserver,
    notifyObservers,
  };
}
